import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync } from '@fortawesome/free-solid-svg-icons';

interface SettingToggleProps {
  apiBaseUrl: string;
  settingKey: string;
  label: string;
  description?: string;
  saveDescription?: string;
}

// A boolean switch bound to a persisted server setting (Setting key/value store).
// Reads on mount (treating 404 as false) and saves immediately on toggle.
// Styled to match OTAEnabledToggle: red when off, green when on.
const SettingToggle: React.FC<SettingToggleProps> = ({
  apiBaseUrl,
  settingKey,
  label,
  description,
  saveDescription,
}) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [updating, setUpdating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const toggleId = `setting-toggle-${settingKey}`;

  useEffect(() => {
    let active = true;
    const fetchSetting = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiBaseUrl}/settings/${settingKey}`, {
          withCredentials: true,
        });
        if (active) {
          setEnabled(response.data?.value === true);
          setError(null);
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          if (active) setEnabled(false);
        } else if (active) {
          console.error(`Error fetching setting ${settingKey}:`, err);
          setError('Failed to load setting');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchSetting();
    return () => {
      active = false;
    };
  }, [apiBaseUrl, settingKey]);

  const toggle = async () => {
    setUpdating(true);
    try {
      const response = await axios.put(
        `${apiBaseUrl}/settings/${settingKey}`,
        { value: !enabled, description: saveDescription || label },
        { withCredentials: true }
      );
      setEnabled(response.data?.value === true);
      setError(null);
    } catch (err: any) {
      console.error(`Error updating setting ${settingKey}:`, err);
      setError('Failed to update setting');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="d-flex align-items-start">
      <div
        className="form-check form-switch"
        style={{
          position: 'relative',
          marginRight: '12px',
          height: '1.5em',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <style>
          {`
            #${toggleId} {
              background-color: #dc3545 !important;
              border-color: #dc3545 !important;
              position: relative;
              z-index: 1;
            }
            #${toggleId}:checked {
              background-color: #198754 !important;
              border-color: #198754 !important;
            }
            #${toggleId}::after {
              background-color: white !important;
            }
            #${toggleId} {
              --bs-form-switch-bg: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='white'/%3e%3c/svg%3e") !important;
            }
          `}
        </style>
        <input
          className="form-check-input"
          type="checkbox"
          role="switch"
          checked={enabled}
          onChange={toggle}
          id={toggleId}
          disabled={updating || loading}
          style={{
            width: '3em',
            height: '1.5em',
            margin: 0,
            opacity: loading || updating ? 0 : 1,
            pointerEvents: loading || updating ? 'none' : 'auto',
            cursor: 'pointer',
          }}
        />
        {(loading || updating) && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
            }}
          >
            <FontAwesomeIcon icon={faSync} spin style={{ fontSize: '1em', marginLeft: '2.5em' }} />
          </div>
        )}
      </div>
      <div>
        <label
          className="form-check-label"
          htmlFor={toggleId}
          style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, cursor: 'pointer' }}
        >
          {label}
        </label>
        {description && <div className="text-muted small mt-1">{description}</div>}
        {error && <div className="text-danger small mt-1">{error}</div>}
      </div>
    </div>
  );
};

export default SettingToggle;
